import { useEffect, useMemo, useState } from 'react';
import { dataService } from '@/services/dataService';
import { Approval } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';

const FILTERS = ['pending', 'approved', 'rejected', 'archived', 'all'] as const;
type FilterKey = (typeof FILTERS)[number];

export function ApprovalsPage() {
  const { t } = useI18n();
  const push = useToast();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Approval | null>(null);

  function reload() {
    dataService.listApprovals().then(setApprovals);
  }
  useEffect(reload, []);

  const visible = useMemo(() => (filter === 'all' ? approvals : approvals.filter((a) => a.status === filter)), [approvals, filter]);

  async function decide(a: Approval, decision: 'approved' | 'rejected') {
    await dataService.decideApproval(a.id, decision);
    push(decision === 'approved' ? t('toast.approvalApproved') : t('toast.approvalRejected'), decision === 'approved' ? 'success' : 'warn');
    reload();
  }
  async function archive(a: Approval) {
    await dataService.archiveApproval(a.id);
    reload();
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    await dataService.deleteApproval(deleteTarget.id);
    push(t('toast.demoRecordDeleted'), 'error');
    setDeleteTarget(null);
    reload();
  }

  return (
    <div>
      <h1>{t('nav.approvals')}</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={filter === f ? 'btn-primary' : ''}>{t(`approvalFilters.${f}`)}</button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>{t('empty.noApprovals')}</div>
      ) : (
        visible.map((a) => (
          <div key={a.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700 }}>{a.actionTitle}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{a.agentId} · {a.targetEntityType} {a.targetEntityId} · {new Date(a.createdAt).toLocaleString()}</div>
              </div>
              <StatusBadge entity="approval" statusKey={a.status} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', margin: '8px 0' }}>{a.reason}</div>
            <div className="cursor-toggle" onClick={() => setOpenId(openId === a.id ? null : a.id)} style={{ fontSize: 12, color: 'var(--info)', cursor: 'pointer', marginBottom: 8 }}>
              {openId === a.id ? t('approvalDetail.hide') : t('approvalDetail.show')}
            </div>
            {openId === a.id && (
              <pre style={{ background: 'var(--surface-2)', padding: 10, borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap' }}>{a.payload}</pre>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {a.status === 'pending' && (
                <>
                  <button className="btn-primary" onClick={() => decide(a, 'approved')}>{t('approvalDetail.approve')}</button>
                  <button style={{ color: 'var(--crit)' }} onClick={() => decide(a, 'rejected')}>{t('approvalDetail.reject')}</button>
                  <button onClick={() => archive(a)}>{t('rowMenu.archive')}</button>
                </>
              )}
              <button style={{ color: 'var(--crit)', marginInlineStart: 'auto' }} onClick={() => setDeleteTarget(a)}>{t('rowMenu.deleteDemo')}</button>
            </div>
          </div>
        ))
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title={t('confirm.deleteApprovalTitle')}
        recordName={deleteTarget?.actionTitle}
        description={t('confirm.deleteApprovalDesc')}
        reversible={false}
        tone="destructive"
        confirmLabel={t('rowMenu.deleteDemo')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
